import {
  IDEMPOTENCY_HEADER,
  classifyOfflineApiError,
  incidentFinalUrl
} from './contract'
import { offlineDb } from './offline-db'
import { markIncidentSynced } from './incident-store'
import { getAccessTokenFromStorage } from './session-store'
import { compressImagesInCrashPayload } from '../utils/crash-image-compress'

const MAX_SYNC_ATTEMPTS = 5
const BASE_BACKOFF_MS = 15 * 1000
const MAX_BACKOFF_MS = 10 * 60 * 1000
const PROCESSING_STALE_MS = 2 * 60 * 1000

function computeNextRetry(attempts) {
  const exp = Math.max(0, attempts - 1)
  const delay = Math.min(BASE_BACKOFF_MS * 2 ** exp, MAX_BACKOFF_MS)
  return new Date(Date.now() + delay).toISOString()
}

function isRetryableEnvelopeError(envelope) {
  const code = Number(envelope?.responseCode || envelope?.statusCode || 0)
  const kind = classifyOfflineApiError(code)
  if (!code || kind === 'unknown') return true
  if (kind === 'conflict' || kind === 'rate_limited' || kind === 'server')
    return true
  return false
}

function isRetryableThrownError(error) {
  const message = (error instanceof Error ? error.message : String(error || ''))
    .toLowerCase()
  return (
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('failed to fetch')
  )
}

async function recoverStaleProcessingJobs() {
  const now = Date.now()
  const processingJobs = await offlineDb.syncQueue
    .where('status')
    .equals('processing')
    .toArray()

  for (const job of processingJobs) {
    if (job?.id == null) continue
    const startedAt = Date.parse(job.processingStartedAt || job.createdAt || '')
    if (Number.isNaN(startedAt)) {
      await offlineDb.syncQueue.update(job.id, {
        status: 'pending',
        processingStartedAt: null,
        nextRetryAt: new Date().toISOString()
      })
      continue
    }
    if (now - startedAt >= PROCESSING_STALE_MS) {
      await offlineDb.syncQueue.update(job.id, {
        status: 'pending',
        processingStartedAt: null,
        nextRetryAt: new Date().toISOString(),
        lastError: 'Recovered stale processing job'
      })
    }
  }
}

/**
 * @param {Function} apiRequest - useHttpApiNew request function
 * @param {Object} [opts]
 * @param {Function} [opts.onProgress]
 * @returns {Promise<{ processed: number, errors: number }>}
 */
export async function processSyncQueue(apiRequest, opts = {}) {
  const { onProgress } = opts
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { processed: 0, errors: 0 }
  }

  await recoverStaleProcessingJobs()

  const now = Date.now()
  const pending = await offlineDb.syncQueue
    .where('status')
    .anyOf('pending', 'error')
    .filter((job) => {
      const nextRetryAtTs = job?.nextRetryAt ? Date.parse(job.nextRetryAt) : 0
      if (!nextRetryAtTs || Number.isNaN(nextRetryAtTs)) return true
      return nextRetryAtTs <= now
    })
    .toArray()

  let processed = 0
  let errors = 0

  for (const job of pending) {
    if (!job?.payload || !job.idempotencyKey || job.id == null) continue

    const attempts = (job.attempts || 0) + 1
    await offlineDb.syncQueue.update(job.id, {
      status: 'processing',
      attempts,
      processingStartedAt: new Date().toISOString()
    })

    const url = incidentFinalUrl()
    const token = getAccessTokenFromStorage()
    if (!token) {
      const terminal = attempts >= MAX_SYNC_ATTEMPTS
      await offlineDb.syncQueue.update(job.id, {
        status: terminal ? 'failed_permanent' : 'error',
        lastError: 'No access token',
        attempts,
        nextRetryAt: terminal ? null : computeNextRetry(attempts),
        deadLetteredAt: terminal ? new Date().toISOString() : null,
        processingStartedAt: null
      })
      errors++
      continue
    }

    const headers = {
      Authorization: `Bearer ${token}`,
      [IDEMPOTENCY_HEADER]: job.idempotencyKey
    }

    try {
      const payload = await compressImagesInCrashPayload(job.payload)
      const resEnvelope = await apiRequest(
        url,
        'POST',
        payload,
        headers,
        { skipAuthRedirect: true, isSyncRequest: true }
      )

      if (!resEnvelope || resEnvelope.responseAnyError) {
        const msg =
          resEnvelope?.responseMessage ||
          resEnvelope?.error ||
          'Sync failed'
        const retryable = isRetryableEnvelopeError(resEnvelope)
        const terminal = !retryable || attempts >= MAX_SYNC_ATTEMPTS
        await offlineDb.syncQueue.update(job.id, {
          status: terminal ? 'failed_permanent' : 'error',
          lastError: String(msg),
          attempts,
          nextRetryAt: terminal ? null : computeNextRetry(attempts),
          deadLetteredAt: terminal ? new Date().toISOString() : null,
          processingStartedAt: null
        })
        errors++
        onProgress?.({ job, error: msg })
        continue
      }

      const rd = resEnvelope.responseData
      const serverId =
        rd && typeof rd === 'object'
          ? rd.crash_id ?? rd.id ?? rd.server_id
          : rd

      await offlineDb.syncQueue.update(job.id, {
        status: 'done',
        lastError: undefined,
        nextRetryAt: null,
        processingStartedAt: null
      })
      await markIncidentSynced(
        job.clientUuid,
        serverId != null ? String(serverId) : 'unknown'
      )
      processed++
      onProgress?.({ job, serverId })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'network'
      const retryable = isRetryableThrownError(e)
      const terminal = !retryable || attempts >= MAX_SYNC_ATTEMPTS
      await offlineDb.syncQueue.update(job.id, {
        status: terminal ? 'failed_permanent' : 'error',
        lastError: msg,
        attempts,
        nextRetryAt: terminal ? null : computeNextRetry(attempts),
        deadLetteredAt: terminal ? new Date().toISOString() : null,
        processingStartedAt: null
      })
      errors++
      onProgress?.({ job, error: msg })
    }
  }

  return { processed, errors }
}
